import { useEffect, useState, useRef } from 'react';
import { Modal, Button } from 'flowbite-react';
import TextBox from "../../../components/forms/text-box";
import TextArea from "../../../components/forms/text-area";
import SelectInput from "../../../components/forms/select";
import Message from "../../../components/toast-message";
import { createScheduler, updateScheduler } from '../../../services/model/scheduler-service';
import { journeyList } from '../../../services/model/journey-service';

function SchedulerSaveModal({ appId, scheduler, onCompletion }) {
  const rootRef = useRef(null);
  const [workflows, setWorkflows] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    targetId: '',
    targetType: 'WORKFLOW',
    scheduleCron: '',
    scheduleType: 'recurrent',
    scheduleAt: '',
    scheduleTimezone: 'UTC'
  });

  const [error, setError] = useState({
    name: false,
    targetId: false,
    scheduleCron: false,
    scheduleType: false,
    scheduleAt: false,
    scheduleTimezone: false
  });

  const [formState, setFormState] = useState({
    processing: false,
    success: false,
    failed: false,
    message: ''
  });

  useEffect(() => {
    // Load available workflows
    async function fetchWorkflows() {
      const { data } = (await journeyList(appId));

      setWorkflows(data.map(workflow => ({
        value: workflow.journeyId,
        label: workflow.name
      })));
    }

    fetchWorkflows();
  }, [appId]);

  useEffect(() => {
    // If editing an existing scheduler, populate the form
    if (scheduler) {
      setFormData({
        name: scheduler.name || '',
        targetId: scheduler.targetId || '',
        targetType: scheduler.targetType || 'WORKFLOW',
        scheduleCron: scheduler.scheduleCron || '',
        scheduleType: scheduler.scheduleType || 'recurrent',
        scheduleAt: scheduler.scheduleAt || '',
        scheduleTimezone: scheduler.scheduleTimezone || 'UTC'
      });
    }
  }, [scheduler]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: value
    }));

    // Clear error for this field if it exists
    if (error[name]) {
      setError(prevError => ({
        ...prevError,
        [name]: false
      }));
    }
  };

  const handleSelectChange = (name, value) => {
    setFormData(prevData => ({
      ...prevData,
      [name]: value
    }));

    // Clear error for this field if it exists
    if (error[name]) {
      setError(prevError => ({
        ...prevError,
        [name]: false
      }));
    }
  };

  const validateForm = () => {
    let valid = true;
    const newErrors = { ...error };

    if (!formData.name.trim()) {
      newErrors.name = true;
      valid = false;
    }

    if (!formData.targetId) {
      newErrors.targetId = true;
      valid = false;
    }

    if (!formData.scheduleType) {
      newErrors.scheduleType = true;
      valid = false;
    }

    if (formData.scheduleType === 'recurrent' && !formData.scheduleCron.trim()) {
      newErrors.scheduleCron = true;
      valid = false;
    }

    if (formData.scheduleType === 'onetime' && !formData.scheduleAt) {
      newErrors.scheduleAt = true;
      valid = false;
    }

    if (!formData.scheduleTimezone) {
      newErrors.scheduleTimezone = true;
      valid = false;
    }

    setError(newErrors);
    return valid;
  };

  const updateFormState = (processing, success, failed, message) => {
    setFormState({ processing, success, failed, message });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!formState.processing && validateForm()) {
      updateFormState(true, false, false);

      try {
        let response, status;

        if (scheduler) {
          // Update existing scheduler
          ({ response, status } = await updateScheduler(appId, scheduler.jobId, formData));
        } else {
          // Create new scheduler
          ({ response, status } = await createScheduler(appId, formData));
        }

        if (status === 200 || status === 201) {
          updateFormState(false, true, false, "Scheduler saved successfully");
          setTimeout(() => {
            onCompletion(true);
          }, 1500);
        } else {
          updateFormState(false, false, true, response?.message || "Failed to save scheduler");
        }
      } catch (error) {
        updateFormState(false, false, true, "An error occurred while saving the scheduler");
      }
    }
  };

  return (
    <div ref={rootRef}>
      <Modal show={true} size="md" popup onClose={() => onCompletion(false)} root={rootRef.current ?? undefined}>
        <Modal.Header />
        <Modal.Body>
          <form onSubmit={handleSubmit}>
            <div className="space-y-6">
              <h3 className="text-xl font-medium text-gray-900">
                {scheduler ? 'Edit Scheduler' : 'Create New Scheduler'}
              </h3>

              {formState.success && <Message text={formState.message} type="success" />}
              {formState.failed && <Message text={formState.message} type="failure" />}

              <TextBox
                type="text"
                name="name"
                id="name"
                label="Name"
                value={formData.name}
                placeholder="Enter scheduler name"
                onChange={handleChange}
                error={error.name}
                errorText="Name is required"
              />

              <SelectInput
                label="Target Workflow"
                name="targetId"
                value={formData.targetId}
                onChange={(e) => handleSelectChange('targetId', e.target.value)}
                options={[{ value: null, label: 'Select Workflow' }, ...workflows]}
                placeholder="Select a workflow"
                error={error.targetId}
                errorText="Workflow is required"
              />

              <SelectInput
                label="Schedule Type"
                name="scheduleType"
                value={formData.scheduleType}
                onChange={(e) => handleSelectChange('scheduleType', e.target.value)}
                options={[
                  { value: 'recurrent', label: 'Recurrent' },
                  { value: 'onetime', label: 'One-time' }
                ]}
                error={error.scheduleType}
                errorText="Schedule Type is required"
              />

              {formData.scheduleType === 'recurrent' && (
                <TextBox
                  type="text"
                  name="scheduleCron"
                  id="scheduleCron"
                  label="Cron Expression"
                  value={formData.scheduleCron}
                  placeholder="e.g. 0 0 * * ? * (daily at midnight)"
                  onChange={handleChange}
                  error={error.scheduleCron}
                  errorText="Cron expression is required"
                />
              )}

              {formData.scheduleType === 'onetime' && (
                <TextBox
                  type="datetime-local"
                  name="scheduleAt"
                  id="scheduleAt"
                  label="Schedule Time"
                  value={formData.scheduleAt}
                  onChange={handleChange}
                  error={error.scheduleAt}
                  errorText="Schedule time is required"
                />
              )}

              <SelectInput
                label="Timezone"
                name="scheduleTimezone"
                value={formData.scheduleTimezone}
                onChange={(e) => handleSelectChange('scheduleTimezone', e.target.value)}
                options={[
                  { value: 'UTC', label: 'UTC' },
                  { value: 'America/New_York', label: 'America/New York' },
                  { value: 'America/Los_Angeles', label: 'America/Los Angeles' },
                  { value: 'Canada/Central', label: 'Canada/Central' },
                  { value: 'Asia/Dubai', label: 'Asia/Dubai' },
                  { value: 'Asia/Colombo', label: 'Asia/Colombo' },
                  { value: 'Asia/Singapore', label: 'Asia/Singapore' }
                ]}
                placeholder="Select Timezone"
                error={error.scheduleTimezone}
                errorText="Timezone is required"
              />

              <div className="w-full flex flex-row gap-2 justify-end">
                <Button color="light" onClick={() => onCompletion(false)}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  isProcessing={formState.processing}
                  disabled={formState.processing}
                >
                  {scheduler ? 'Update' : 'Create'}
                </Button>
              </div>
            </div>
          </form>
        </Modal.Body>
      </Modal>
    </div>
  );
}

export default SchedulerSaveModal;
